import { useCallback, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { useField } from "react-final-form";

import Icon from "@/components/icon/icon";
import { SortableItem, SortableList } from "@/features/admin/components/sortable-list";
import StaticOverlayCard from "@/features/widget-forms/forms/weather-model-map/components/static-overlay-card";
import AddStaticOverlayModal from "@/features/widget-forms/forms/weather-model-map/modals/add-static-overlay-modal";
import { useStaticMapOverlaysQuery } from "@/features/widget-forms/forms/weather-model-map/queries";
import styles from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog.module.css";
import type { StaticMapOverlaysOverlayConfiguration } from "@/lib/api";
import { toMoved } from "@/lib/array";
import { useTranslation } from "@/lib/i18n";

const NO_OVERLAYS: StaticMapOverlaysOverlayConfiguration[] = [];

/**
 * Static overlays on the risk picture map, stored in the `staticOverlays` field of the
 * enclosing Final Form. Must render outside that form's `<form>` element: the add/configure
 * modals have their own `<form>`, and React bubbles their submit through the portal.
 */
export function WarningPageStaticOverlays() {
  const { t } = useTranslation();
  const { input } = useField<StaticMapOverlaysOverlayConfiguration[]>("staticOverlays", {
    subscription: { value: true },
  });
  const configurations = input.value || NO_OVERLAYS;
  const onChange = input.onChange;
  const { data: staticOverlays } = useStaticMapOverlaysQuery();

  const selectedIds = useMemo(
    () => configurations.map((c) => c.static_map_overlay_id),
    [configurations],
  );

  const update = useCallback(
    (id: string, patch: Partial<StaticMapOverlaysOverlayConfiguration>) =>
      onChange(
        configurations.map((c) =>
          c.static_map_overlay_id === id ? { ...c, ...patch } : c,
        ),
      ),
    [configurations, onChange],
  );

  const add = (overlay: StaticMapOverlaysOverlayConfiguration) => {
    if (selectedIds.includes(overlay.static_map_overlay_id)) return;
    onChange([...configurations, overlay]);
  };

  const rename = (id: string, label: string) =>
    update(id, {
      // Blank label falls back to the catalog name, same as the map widget forms.
      label: label.trim() || staticOverlays?.find((o) => o.id === id)?.name || id,
    });

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography>
          {t("admin.dashboard_settings.warning_pages.static_overlays.title")}
        </Typography>
        <AddStaticOverlayModal
          staticOverlays={staticOverlays ?? []}
          selectedStaticOverlayIds={selectedIds}
          onAdd={add}
        />
      </Stack>
      {configurations.length > 0 ? (
        <SortableList
          onReorder={(from, to) => onChange(toMoved(configurations, from, to))}
          className={styles.cardGrid}
          groupId="warning-page-static-overlays"
        >
          {configurations.map((overlay, index) => (
            <SortableItem key={overlay.static_map_overlay_id} index={index}>
              <StaticOverlayCard
                config={overlay}
                overlayInfo={staticOverlays?.find(
                  (o) => o.id === overlay.static_map_overlay_id,
                )}
                onRename={(label) => rename(overlay.static_map_overlay_id, label)}
                onColorChange={(color) => update(overlay.static_map_overlay_id, { color })}
                onEnabledByDefaultChange={(enabled) =>
                  update(overlay.static_map_overlay_id, { enabled_by_default: enabled })
                }
                onRemove={() =>
                  onChange(
                    configurations.filter(
                      (c) => c.static_map_overlay_id !== overlay.static_map_overlay_id,
                    ),
                  )
                }
              />
            </SortableItem>
          ))}
        </SortableList>
      ) : (
        <div className={styles.emptyState}>
          <Icon name="shapes" size={32} />
          <span className={styles.emptyStateText}>
            {t("admin.dashboard_settings.warning_pages.static_overlays.empty")}
          </span>
        </div>
      )}
    </Stack>
  );
}
